import React, { useRef, useState } from 'react';

import classNames from 'classnames';

import './TextField.scoped.scss';

import fieldErrorParser, { FieldErrorParserInterface } from 'utils/fieldErrorParser';

import LabelWithErrors from '../LabelWithErrors/LabelWithErrors';

// Reusable text input component, can be single line or multi line. Supports auto growth
// Example:

//  <TextField
//    multiLine                         // If enabled, renders textarea
//    value={comment}                   // Value of input
//    startIcon={<Icon/>}               // Icon used as prefix
//    onChange={e => {                  // Handler, supplies event
//      setComment(e.target.value);
//    }}
//  />

interface Props extends FieldErrorParserInterface {
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  multiLine?: boolean;
  type?: string;
  label?: string;
}

const TextField: React.FC<
  Props & React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>
> = ({
  onChange,
  startIcon = null,
  endIcon = null,
  disabled = false,
  multiLine = false,
  type = 'text',
  label,
  formIsTouched = true,
  errors = [],
  property,
  ...rest
}) => {
  const wrapperNode = useRef(null);
  const textAreaNode = useRef(null);
  const [fieldIsTouched, setFieldIsTouched] = useState(false);

  const onChangeHandler = e => {
    if (textAreaNode.current && wrapperNode.current) {
      textAreaNode.current.style.height = 'auto';

      const scrollHeight = textAreaNode.current.scrollHeight - 5;

      textAreaNode.current.style.height = `${scrollHeight}px`;
    }

    if (onChange) {
      onChange(e);
    }
  };

  const onBlur = () => {
    setFieldIsTouched(true);
  };

  const errorInfo = fieldErrorParser({
    errors,
    fieldIsTouched,
    formIsTouched,
    label,
    property,
  });

  return (
    <div>
      <LabelWithErrors errorInfo={errorInfo} label={label} />
      <label
        ref={wrapperNode}
        className={classNames({
          textField: true,
          start: startIcon,
          end: endIcon,
          disabled,
          errorInput: errorInfo.showFirstError,
        })}
      >
        {startIcon && <div className="icon-container start">{startIcon}</div>}
        {multiLine && (
          <textarea
            rows={1}
            ref={textAreaNode}
            onInput={onChangeHandler}
            {...rest}
            onBlur={onBlur}
          />
        )}
        {!multiLine && <input onChange={onChange} type={type} {...rest} onBlur={onBlur} />}
        {endIcon && <div className="icon-container end">{endIcon}</div>}
      </label>
    </div>
  );
};

export default TextField;
